// src/utils/markdown.ts
//
// Rendu du MARKDOWN produit par l'assistant IA.
//
// ═══ Pourquoi ═════════════════════════════════════════════════════════════
// Le modèle répond en Markdown — titres, listes, gras, et surtout des tableaux
// GFM pour énumérer des modules ou des chiffres. La bulle de conversation
// affichait ce texte tel quel dans un `<p>` : le lecteur voyait les astérisques
// et les barres verticales au lieu d'un tableau.
//
// ═══ Sécurité ═════════════════════════════════════════════════════════════
// Une réponse de modèle est du contenu non fiable : elle peut contenir du HTML
// brut, que Markdown laisse passer par conception. Le HTML produit repasse donc
// systématiquement par `htmlSain()` — le seul point d'entrée autorisé pour
// `dangerouslySetInnerHTML` dans ce projet (cf. utils/htmlSain.ts). Rien n'est
// affiché sans cette désinfection.

import { marked } from 'marked';
import { htmlSain } from './htmlSain';

// `gfm` apporte les tableaux et les barrés ; `breaks` fait d'un simple retour
// à la ligne un `<br>`, comme l'attend une conversation — sans lui, le modèle
// devrait doubler ses sauts de ligne pour être lisible.
marked.use({ gfm: true, breaks: true });

/**
 * Markdown → HTML désinfecté.
 *
 * `async: false` force la variante synchrone de `marked` : sans cela sa
 * signature rend `string | Promise<string>`, inutilisable dans un rendu React.
 * Si l'analyse échoue, on retombe sur le texte brut échappé — le lecteur voit
 * alors la réponse non formatée, jamais une bulle vide.
 */
export function markdownEnHtml(texte?: string | null): string {
  if (!texte) return '';
  try {
    const html = marked.parse(reparerTableaux(texte), { async: false }) as string;
    return htmlSain(html);
  } catch {
    return htmlSain(echapper(texte).replace(/\n/g, '<br>'));
  }
}

/**
 * Rend exploitables les pseudo-tableaux du modèle.
 *
 * L'assistant énumère volontiers en écrivant des lignes « | Module | Contenu | »
 * sans la ligne de séparation « |---|---| » qu'exige GFM. Sans elle, ce n'est
 * pas un tableau : marked en fait un paragraphe, et le lecteur voit les barres
 * verticales — c'est précisément ce qui était affiché.
 *
 * Plutôt que de compter sur le modèle pour se corriger, on insère la ligne
 * manquante : tout bloc d'au moins deux lignes encadrées de barres et non
 * suivies d'un séparateur en reçoit un, calculé sur le nombre de colonnes de
 * sa première ligne. Une ligne isolée n'est pas touchée — ce n'est pas un
 * tableau.
 */
function reparerTableaux(texte: string): string {
  const lignes = texte.split('\n');
  const sortie: string[] = [];
  const estLigneTableau = (l: string) => {
    const t = l.trim();
    return t.startsWith('|') && t.endsWith('|') && t.length > 2;
  };
  const estSeparateur = (l: string) => /^\|[\s:|-]+\|$/.test(l.trim());

  let i = 0;
  while (i < lignes.length) {
    if (!estLigneTableau(lignes[i])) {
      sortie.push(lignes[i]);
      i += 1;
      continue;
    }
    // Bloc de lignes encadrées de barres.
    let j = i;
    while (j < lignes.length && estLigneTableau(lignes[j])) j += 1;
    const bloc = lignes.slice(i, j);

    if (bloc.length >= 2 && !estSeparateur(bloc[1])) {
      // Nombre de colonnes = cellules de la première ligne, barres extérieures
      // retirées. Une cellule vide compte : le tableau doit rester aligné.
      const colonnes = bloc[0].trim().slice(1, -1).split('|').length;
      sortie.push(bloc[0]);
      sortie.push(`|${' --- |'.repeat(colonnes)}`);
      sortie.push(...bloc.slice(1));
    } else {
      sortie.push(...bloc);
    }
    i = j;
  }
  return sortie.join('\n');
}

/** Prêt pour `dangerouslySetInnerHTML`. */
export function markdownEnHtmlProp(texte?: string | null): { __html: string } {
  return { __html: markdownEnHtml(texte) };
}

function echapper(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Classes de mise en forme du HTML rendu, à poser sur le conteneur.
 *
 * Écrites en variantes d'attribut Tailwind plutôt qu'avec le plugin
 * `typography` : celui-ci n'est pas installé, et ses tailles par défaut sont
 * pensées pour une page, pas pour une bulle de 300 pixels de large.
 *
 * Le tableau est le cas qui a motivé tout ceci : il doit pouvoir défiler
 * horizontalement sans pousser la bulle hors de l'écran.
 */
export const CLASSES_MARKDOWN = [
  'text-sm text-gray-800 break-words',
  // Titres — deux tailles suffisent dans une conversation.
  '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1.5',
  '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-2 [&_h4]:mb-0.5',
  // Paragraphes et séparateurs.
  '[&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_hr]:my-2.5 [&_hr]:border-gray-200',
  // Listes — la puce doit rester visible dans une bulle étroite.
  '[&_ul]:my-1.5 [&_ul]:pl-4 [&_ul]:list-disc',
  '[&_ol]:my-1.5 [&_ol]:pl-4 [&_ol]:list-decimal',
  '[&_li]:my-0.5 [&_li]:leading-snug',
  // Emphase.
  '[&_strong]:font-semibold [&_strong]:text-gray-900',
  '[&_em]:italic',
  // Liens.
  '[&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-700',
  // Code.
  '[&_code]:bg-gray-100 [&_code]:text-[12px] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded',
  '[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:text-[12px] [&_pre]:p-2.5 [&_pre]:rounded-lg',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto',
  '[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0',
  // Citations.
  '[&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-2.5',
  '[&_blockquote]:text-gray-600 [&_blockquote]:my-2',
  // Tableaux — le motif de ce module.
  '[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto',
  '[&_table]:my-2 [&_table]:text-[12px] [&_table]:border-collapse',
  '[&_thead]:bg-gray-50',
  '[&_th]:border [&_th]:border-gray-200 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_th]:whitespace-nowrap',
  '[&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_td]:align-top',
].join(' ');
